import { serviceCountryName } from './serviceRequestCountries.js';

export const S14_STRATEGIES = ['Hot standby', 'Warm standby', 'Cold recovery'] as const;
export const S14_TRIGGERS = ['CMP Priority 1', 'CMP Priority 2', 'EPP escalation', 'Critical function outage', 'Supplier disruption'] as const;
export const S14_REVIEW_FREQUENCIES = ['Quarterly', 'Every six months', 'Annually'] as const;
export const S14_EXERCISE_TYPES = ['Tabletop', 'Functional exercise', 'Full invocation'] as const;
export const S14_SCENARIOS = ['Location loss', 'IT or cyber failure', 'Key staff loss', 'Supplier disruption', 'Regulatory suspension', 'Pandemic or mass casualty'] as const;

export type S14Function = { name: string; rtoHours: string; mtpdHours: string; strategy: string;
    scenarioMitigations: Record<string, string> };
export type S14Intake = {
    organizationName: string;
    industry: string;
    sites: string[];
    criticalFunctions: S14Function[];
    alternativeLocations: string;
    bcmTeam: string;
    criticalSuppliers: string;
    regulatoryNotifications: string;
    activationTriggers: string[];
    reviewFrequency: string;
    exerciseType: string;
    templateContext?: string;
};

const text = (value: unknown, maximum = 4000) => String(value ?? '').trim().slice(0, maximum);
const strings = (value: unknown, maximum = 20) => Array.isArray(value)
    ? [...new Set(value.map(item => text(item, 240)).filter(Boolean))].slice(0, maximum) : [];

export function emptyS14Intake(): S14Intake {
    return { organizationName: '', industry: '', sites: [], criticalFunctions: [], alternativeLocations: '',
        bcmTeam: '', criticalSuppliers: '', regulatoryNotifications: '', activationTriggers: [],
        reviewFrequency: '', exerciseType: '' };
}

export function suggestedS14Functions(industry: string): string[] {
    const sector = industry.toLowerCase();
    if (/bank|financ|invest|insur|trading/.test(sector))
        return ['Client operations', 'Payments and settlement', 'Regulatory reporting', 'IT and data systems', 'Finance and treasury'];
    if (/extract|energy|mining|oil|gas|manufactur/.test(sector))
        return ['Production', 'Supply chain and logistics', 'Safety systems', 'IT and data systems', 'Finance and treasury'];
    if (/ngo|nonprofit|non-profit|humanitarian/.test(sector))
        return ['Programme delivery', 'Staff safety', 'Donor reporting', 'IT and communications', 'Finance and treasury'];
    return ['Client operations', 'Supply chain and logistics', 'IT and data systems', 'Finance and treasury', 'People operations'];
}

export function prefillS14Intake(organization: any): S14Intake {
    const address = (value: any) => [value?.addressLine1, value?.addressLine2, value?.city,
        value?.administrativeArea, serviceCountryName(value?.country || value?.countryCode)]
        .map(part => text(part, 160)).filter(Boolean).join(', ');
    return { ...emptyS14Intake(), organizationName: text(organization?.name || organization?.brandName, 240),
        industry: text(organization?.industry, 160), sites: strings([
            address(organization?.headquartersAddress),
            ...(Array.isArray(organization?.additionalLocations) ? organization.additionalLocations.map(address) : []),
        ]) };
}

export function cleanS14Intake(source: any): S14Intake {
    const input = source && typeof source === 'object' ? source : {};
    return {
        organizationName: text(input.organizationName, 240), industry: text(input.industry, 160),
        sites: strings(input.sites),
        criticalFunctions: (Array.isArray(input.criticalFunctions) ? input.criticalFunctions : []).slice(0, 20)
            .map((item: any) => ({ name: text(item?.name, 160), rtoHours: text(item?.rtoHours, 20),
                mtpdHours: text(item?.mtpdHours, 20), strategy: S14_STRATEGIES.includes(item?.strategy) ? item.strategy : '',
                scenarioMitigations: Object.fromEntries(S14_SCENARIOS.map(scenario =>
                    [scenario, text(item?.scenarioMitigations?.[scenario], 1000)])) })),
        alternativeLocations: text(input.alternativeLocations), bcmTeam: text(input.bcmTeam),
        criticalSuppliers: text(input.criticalSuppliers), regulatoryNotifications: text(input.regulatoryNotifications),
        activationTriggers: strings(input.activationTriggers).filter(item => S14_TRIGGERS.includes(item as any)),
        reviewFrequency: S14_REVIEW_FREQUENCIES.includes(input.reviewFrequency) ? input.reviewFrequency : '',
        exerciseType: S14_EXERCISE_TYPES.includes(input.exerciseType) ? input.exerciseType : '',
        ...(text(input.templateContext, 20000) ? { templateContext: text(input.templateContext, 20000) } : {}),
    };
}

export function missingS14Intake(source: any): string[] {
    const intake = cleanS14Intake(source);
    const missing: string[] = [];
    if (!intake.organizationName || !intake.industry)
        missing.push('Organisation and industry');
    if (!intake.sites.length)
        missing.push('Operating sites');
    if (!intake.criticalFunctions.length || intake.criticalFunctions.some(item => !item.name)
        || new Set(intake.criticalFunctions.map(item => item.name.toLowerCase())).size !== intake.criticalFunctions.length)
        missing.push('Critical functions');
    if (intake.criticalFunctions.some(item => !Number.isFinite(Number(item.rtoHours)) || Number(item.rtoHours) <= 0
        || !Number.isFinite(Number(item.mtpdHours)) || Number(item.mtpdHours) < Number(item.rtoHours)))
        missing.push('RTO and MTPD per function');
    if (intake.criticalFunctions.some(item => !item.strategy))
        missing.push('Recovery strategy per function');
    if (intake.criticalFunctions.some(item => S14_SCENARIOS.some(scenario => !item.scenarioMitigations[scenario])))
        missing.push('Six disruption mitigations per function');
    if (!intake.alternativeLocations)
        missing.push('Alternative work locations');
    if (!intake.bcmTeam)
        missing.push('BCM team and roles');
    if (!intake.criticalSuppliers)
        missing.push('Supplier decision');
    if (!intake.regulatoryNotifications)
        missing.push('Regulatory notification decision');
    if (!intake.activationTriggers.length)
        missing.push('Activation triggers');
    if (!intake.reviewFrequency || !intake.exerciseType)
        missing.push('Review and exercise schedule');
    return missing;
}
